/**
 * Cleaner lifecycle / checklist access verification (stored on checklists.access_verification_settings).
 * Guest count: use reservations.guest_count — set via manual reservation or future PMS; iCal rarely includes counts.
 */
export type ChecklistEntryType = "lockbox" | "smart_lock" | "key_handoff" | "other";

export interface AccessVerificationSettings {
  /** Default entry type cleaners confirm on arrival */
  default_entry_type?: ChecklistEntryType | null;
  /** When true, cleaners must confirm they have their own key before continuing. */
  cleaner_has_own_key?: boolean;
  backup_key_required?: boolean;
  backup_key_proof_required?: boolean;
  notes?: string | null;
  /** Owner-uploaded reference images showing where keys are (cleaners match these in arrival flow). */
  key_location_reference_urls?: string[];
}

export const DEFAULT_ACCESS_VERIFICATION_SETTINGS: AccessVerificationSettings = {
  default_entry_type: "lockbox",
  cleaner_has_own_key: false,
  backup_key_required: false,
  backup_key_proof_required: false,
  notes: null,
  key_location_reference_urls: [],
};

function parseUrlStringArray(raw: unknown): string[] {
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("[") || t.startsWith("{")) {
      try {
        return parseUrlStringArray(JSON.parse(t) as unknown);
      } catch {
        /* ignore */
      }
    }
    if (t.length > 0 && (t.startsWith("http") || t.startsWith("/"))) return [t];
    return [];
  }
  if (!Array.isArray(raw)) {
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      return Object.values(raw as Record<string, unknown>).filter(
        (x): x is string => typeof x === "string" && x.trim().length > 0
      );
    }
    return [];
  }
  return raw.filter((x): x is string => typeof x === "string" && x.trim().length > 0);
}

export function parseAccessVerificationSettings(raw: unknown): AccessVerificationSettings {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_ACCESS_VERIFICATION_SETTINGS };
  const o = raw as Record<string, unknown>;
  const entry = o.default_entry_type;
  const allowed: ChecklistEntryType[] = ["lockbox", "smart_lock", "key_handoff", "other"];
  return {
    default_entry_type:
      typeof entry === "string" && (allowed as string[]).includes(entry)
        ? (entry as ChecklistEntryType)
        : DEFAULT_ACCESS_VERIFICATION_SETTINGS.default_entry_type,
    cleaner_has_own_key: Boolean(o.cleaner_has_own_key),
    backup_key_required: Boolean(o.backup_key_required),
    backup_key_proof_required: Boolean(o.backup_key_proof_required),
    notes: typeof o.notes === "string" ? o.notes : null,
    key_location_reference_urls: parseUrlStringArray(o.key_location_reference_urls),
  };
}

export type InventoryScope = "unit" | "guest_amenities" | "supply_closet";

export const INVENTORY_SCOPE_LABELS: Record<InventoryScope, string> = {
  unit: "Unit (entry walkthrough)",
  guest_amenities: "Guest amenities (after cleaning)",
  supply_closet: "Supply closet (after amenities)",
};

/** NULL legacy rows count as guest amenities for post-clean inventory. */
export function resolveInventoryScope(raw: string | null | undefined): Exclude<InventoryScope, "unit"> {
  if (raw === "supply_closet") return "supply_closet";
  return "guest_amenities";
}

export interface EntryWalkthroughAccessPayload {
  entry_type: ChecklistEntryType;
  /** Cleaner confirmed they are using their own key for this property. */
  own_key_confirmed?: boolean;
  backup_key_proof_urls?: string[];
  /** Cleaner photo proving primary keys are at the location (required when checklist has key reference images). */
  primary_key_proof_urls?: string[];
}

/** Persisted on `cleaning_tasks.entry_walkthrough_state` (jsonb). */
export interface EntryWalkthroughState {
  access?: EntryWalkthroughAccessPayload | Record<string, unknown>;
  /** Legacy: condition photos keyed by room name when checklist has no proof standards. */
  condition_photos_by_room?: Record<string, string[]>;
  /** Proof URLs keyed by `proof_standards.id` (captured at entry; merged into submission). */
  proof_media_by_standard_id?: Record<string, string[]>;
  /** Standard IDs marked "pass" for `functional_check` type standards (no photo required). */
  functional_check_pass_standard_ids?: string[];
  completed_at?: string;
}

/** Extract `proof_media_by_standard_id` from stored entry walkthrough JSON. */
export function parseProofMediaByStandardIdFromEntryState(raw: unknown): Record<string, string[]> {
  if (raw == null || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  const pm = o.proof_media_by_standard_id;
  if (pm == null || typeof pm !== "object" || Array.isArray(pm)) return {};
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(pm)) {
    if (!Array.isArray(v)) continue;
    const urls = v.filter((x): x is string => typeof x === "string");
    if (urls.length) out[k] = urls;
  }
  return out;
}

/** Persisted on `cleaning_tasks.guest_setup_state` (jsonb). */
export interface GuestSetupState {
  proof_urls_by_room_id?: Record<string, string[]>;
  /** Per checklist staging line id (when using checklist guest_amenity_staging). */
  proof_urls_by_staging_id?: Record<string, string[]>;
  /** Slice 44 — computed leave-out qty per staging line at submit time. */
  left_out_by_staging_id?: Record<string, number>;
  /** Guest count used for leave-out math (null when unknown / minimum fallback). */
  guest_count_used?: number | null;
  guest_count_unknown?: boolean;
}

export function parseGuestSetupState(raw: unknown): GuestSetupState {
  if (raw == null || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  const out: GuestSetupState = {};

  const pm = o.proof_urls_by_room_id;
  if (pm != null && typeof pm === "object" && !Array.isArray(pm)) {
    const roomOut: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(pm)) {
      if (!Array.isArray(v)) continue;
      const urls = v.filter((x): x is string => typeof x === "string" && x.length > 0);
      if (urls.length) roomOut[k] = urls;
    }
    if (Object.keys(roomOut).length > 0) out.proof_urls_by_room_id = roomOut;
  }

  const ps = o.proof_urls_by_staging_id;
  if (ps != null && typeof ps === "object" && !Array.isArray(ps)) {
    const stagingOut: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(ps)) {
      if (!Array.isArray(v)) continue;
      const urls = v.filter((x): x is string => typeof x === "string" && x.length > 0);
      if (urls.length) stagingOut[k] = urls;
    }
    if (Object.keys(stagingOut).length > 0) out.proof_urls_by_staging_id = stagingOut;
  }

  const left = o.left_out_by_staging_id;
  if (left != null && typeof left === "object" && !Array.isArray(left)) {
    const leftOut: Record<string, number> = {};
    for (const [k, v] of Object.entries(left)) {
      if (typeof v === "number" && Number.isFinite(v) && v >= 0) leftOut[k] = Math.floor(v);
    }
    if (Object.keys(leftOut).length > 0) out.left_out_by_staging_id = leftOut;
  }

  if (typeof o.guest_count_used === "number" && Number.isFinite(o.guest_count_used)) {
    out.guest_count_used = Math.floor(o.guest_count_used);
  } else if (o.guest_count_used === null) {
    out.guest_count_used = null;
  }

  if (typeof o.guest_count_unknown === "boolean") {
    out.guest_count_unknown = o.guest_count_unknown;
  }

  return out;
}

/** Extract functional-check "pass" standard IDs from stored entry walkthrough JSON. */
export function parseFunctionalPassStandardIdsFromEntryState(raw: unknown): Set<string> {
  if (raw == null || typeof raw !== "object") return new Set();
  const o = raw as Record<string, unknown>;
  const ids = o.functional_check_pass_standard_ids;
  if (!Array.isArray(ids)) return new Set();
  return new Set(ids.filter((x): x is string => typeof x === "string" && x.length > 0));
}

export interface CloseoutPayload {
  keys_returned_confirmed: boolean;
  departure_notes?: string | null;
  keys_photo_url?: string | null;
}
